'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import allNutrients from '@tables/nutrients';
import nutrientOrder from '@tables/nutrient-order';
import { getMealSearchParamsForProfile } from '@pro/utils/Patients';
import { getQueryFromParams } from '@utils/Search';

const ProfileNutritionTab = ({ profile }) => {
    // Helper function to format numeric values with max 2 decimal places
    const formatNumber = (value) => {
        if (value === null || value === undefined) return value;
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        // Round to 2 decimal places and remove trailing zeros
        return parseFloat(num.toFixed(2));
    };

    // Helper function to render nutrient values (from ResolvedProfileDetails)
    const renderNutrientValue = (nutrient, data) => {
        if (!data) return null;

        // Check for explicit values
        const explicitMin = typeof data.min !== 'undefined';
        const explicitMax = typeof data.max !== 'undefined';

        // Check for implicit values
        const hasImplicit = data.implicit && (
            typeof data.implicit.min !== 'undefined' ||
            typeof data.implicit.max !== 'undefined'
        );

        // If no constraints at all, this is a tracked nutrient
        if (!explicitMin && !explicitMax && !hasImplicit) {
            return (
                <div className="nutrient-value tracked">
                    <span className="tracked-label">Tracked</span>
                </div>
            );
        }

        let minValue = null;
        let maxValue = null;
        let minIsImplicit = false;
        let maxIsImplicit = false;

        // Get values with precedence given to explicit values
        if (explicitMin) {
            minValue = data.min;
            minIsImplicit = false;
        } else if (hasImplicit && typeof data.implicit.min !== 'undefined') {
            minValue = data.implicit.min;
            minIsImplicit = true;
        }

        if (explicitMax) {
            maxValue = data.max;
            maxIsImplicit = false;
        } else if (hasImplicit && typeof data.implicit.max !== 'undefined') {
            maxValue = data.implicit.max;
            maxIsImplicit = true;
        }

        // Special case for "NO X" nutrients (min undefined/0 and max=0)
        if ((typeof minValue === 'undefined' || minValue === 0) && maxValue === 0) {
            return (
                <div className="nutrient-value no-value">
                    <span className="no-label">NO</span>
                </div>
            );
        }

        // Handle different combinations of min/max values
        if (minValue !== null && maxValue !== null) {
            // Both min and max
            return (
                <div className="nutrient-value range">
                    <span className={classNames("min-value", { implicit: minIsImplicit })}>
                        {formatNumber(minValue)}
                    </span>
                    <span className="range-separator">—</span>
                    <span className={classNames("max-value", { implicit: maxIsImplicit })}>
                        {formatNumber(maxValue)}
                    </span>
                    <span className="units">{allNutrients[nutrient]?.Units}</span>
                </div>
            );
        } else if (minValue !== null) {
            // Only min value
            return (
                <div className="nutrient-value min-only">
                    <span className={classNames("min-value", { implicit: minIsImplicit })}>
                        {formatNumber(minValue)}
                    </span>
                    <span className="units">{allNutrients[nutrient]?.Units}</span>
                    <span className="min-label">MIN</span>
                </div>
            );
        } else if (maxValue !== null) {
            // Only max value
            return (
                <div className="nutrient-value max-only">
                    <span className={classNames("max-value", { implicit: maxIsImplicit })}>
                        {formatNumber(maxValue)}
                    </span>
                    <span className="units">{allNutrients[nutrient]?.Units}</span>
                    <span className="max-label">MAX</span>
                </div>
            );
        }

        return null;
    };

    const renderNutrientRow = (nutrNo, data, showAll = true) => {
        const nutrient = allNutrients[nutrNo];
        if (!nutrient) return null;

        const hasConstraints = data && (
            typeof data.min !== 'undefined' ||
            typeof data.max !== 'undefined' ||
            (data.implicit && (
                typeof data.implicit.min !== 'undefined' ||
                typeof data.implicit.max !== 'undefined'))
        );

        // Skip nutrients without constraints if not showing all
        if (!showAll && !hasConstraints) {
            return null;
        }

        return (
            <tr key={nutrNo} className="nutrient-row">
                <td className="nutrient-name">
                    <strong>{nutrient.NutrDesc}</strong>
                    {(data?.implicit) && (
                        <span className="implicit-indicator" title="Has implicit constraints">*</span>
                    )}
                </td>
                <td className="nutrient-value-cell">
                    {renderNutrientValue(nutrNo, data)}
                </td>
            </tr>
        );
    };

    const renderEnvelope = (envelope, showAll = false) => {
        if (!envelope) return null;

        return (
            <table className="envelope-table">
                <tbody>
                    {Object.keys(envelope)
                        .sort((a, b) => nutrientOrder.indexOf(a) - nutrientOrder.indexOf(b))
                        .map(nutrNo => renderNutrientRow(nutrNo, envelope[nutrNo], showAll))}
                </tbody>
            </table>
        );
    };

    const getPublisherSearchUrl = (mealType) => {
        // Create a deep copy of the mealType with promoted implicit values
        const modifiedMealType = JSON.parse(JSON.stringify(mealType));
        
        // Promote implicit values in the mealType envelope
        if (modifiedMealType.envelope) {
            const promotedEnvelope = {};
            Object.entries(modifiedMealType.envelope).forEach(([nutrNo, data]) => {
                promotedEnvelope[nutrNo] = { ...data };
                if (typeof data.min === 'undefined' && data.implicit && typeof data.implicit.min !== 'undefined') {
                    promotedEnvelope[nutrNo].min = data.implicit.min;
                }
                if (typeof data.max === 'undefined' && data.implicit && typeof data.implicit.max !== 'undefined') {
                    promotedEnvelope[nutrNo].max = data.implicit.max;
                }
                delete promotedEnvelope[nutrNo].implicit;
            });
            modifiedMealType.envelope = promotedEnvelope;
        }
        
        // Generate search parameters using the utility function with the modified mealType
        const searchParams = getMealSearchParamsForProfile(modifiedMealType, profile);
        
        // Convert to a query string
        const query = getQueryFromParams(searchParams);
        
        // Build the URL with proper query parameter formatting
        const queryParams = [];
        
        Object.entries(query).forEach(([key, value]) => {
            // Handle array values by repeating the key for each value
            if (Array.isArray(value)) {
                value.forEach(item => {
                    queryParams.push(`${key}=${encodeURIComponent(item)}`);
                });
            } else {
                queryParams.push(`${key}=${encodeURIComponent(value)}`);
            }
        });
        
        return `/publisher?${queryParams.join('&')}`;
    };

    const openPublisherSearch = (e, mealType) => {
        e.stopPropagation();
        const url = getPublisherSearchUrl(mealType);
        window.open(url, '_blank');
    };

    return (
        <div className="profile-section">
            <div className="nutrition-grid">
                <div className="nutrition-card">
                    <h4><i className="feather feather-activity" /> Nutrition Display</h4>
                    <div className="nutrition-setting">
                        <span className="setting-label">Hide Nutrition:</span>
                        <span className={`setting-value ${profile.hide_nutrition ? 'enabled' : 'disabled'}`}>
                            {profile.hide_nutrition ? '✓ Hidden' : '✗ Visible'}
                        </span>
                    </div>
                    <div className="nutrition-setting">
                        <span className="setting-label">RD Override:</span>
                        <span className={`setting-value ${profile.preferences?.rd_override ? 'enabled' : 'disabled'}`}>
                            {profile.preferences?.rd_override ? '✓ Enabled' : '✗ Disabled'}
                        </span>
                    </div>
                </div>
                
                <div className="nutrition-card">
                    <h4><i className="feather feather-target" /> Targets & Portions</h4>
                    <div className="nutrition-setting">
                        <span className="setting-label">Default Portion:</span>
                        <span className="setting-value">{profile.portion || 'Standard'}</span>
                    </div>
                    <div className="nutrition-setting">
                        <span className="setting-label">Target Energy:</span>
                        <span className="setting-value">{profile.target_energy_kcal || profile.target_energy || 'Not set'}</span>
                    </div>
                    {profile.nutrient_targets && (
                        <div className="nutrition-setting">
                            <span className="setting-label">Nutrient Targets:</span>
                            <span className="setting-value">{Object.keys(profile.nutrient_targets).length} targets</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Daily Totals Section */}
            {profile.preferences?.daily_totals && (
                <div className="nutrition-targets-section">
                    <h4><i className="feather feather-bar-chart" /> Daily Nutrition Targets</h4>
                    <div className="nutrition-table-container">
                        {renderEnvelope(profile.preferences.daily_totals, false)}
                    </div>
                </div>
            )}

            {/* Meal Types Section */}
            {profile.preferences?.meal_types && profile.preferences.meal_types.length > 0 && (
                <div className="meal-types-section">
                    <h4><i className="feather feather-clock" /> Meal Type Constraints</h4>
                    {profile.preferences.meal_types.map((mealType, index) => (
                        <div key={index} className="meal-type-card">
                            <div className="meal-type-header">
                                <h5>{mealType.name}</h5>
                                <div className="meal-controls">
                                    <button
                                        className="publisher-search-btn"
                                        onClick={(e) => openPublisherSearch(e, mealType)}
                                        title={`Search recipes for ${mealType.name}`}
                                    >
                                        <i className="feather feather-search" /> Search Recipes
                                    </button>
                                </div>
                            </div>
                            <div className="meal-type-details">
                                <div className="meal-info-grid">
                                    <div className="meal-info-item">
                                        <span className="info-label">Time:</span>
                                        <span className="info-value">
                                            {Math.floor(mealType.begin / 60)}:{String(mealType.begin % 60).padStart(2, '0')} -
                                            {Math.floor(mealType.end / 60)}:{String(mealType.end % 60).padStart(2, '0')}
                                        </span>
                                    </div>
                                    <div className="meal-info-item">
                                        <span className="info-label">Main Dish:</span>
                                        <span className="info-value">{mealType.main_dish || 'Not specified'}</span>
                                    </div>
                                    {mealType.side_dish && (
                                        <div className="meal-info-item">
                                            <span className="info-label">Side Dish:</span>
                                            <span className="info-value">{mealType.side_dish}</span>
                                        </div>
                                    )}
                                    <div className="meal-info-item">
                                        <span className="info-label">Participants:</span>
                                        <span className="info-value">
                                            {mealType.participants?.length > 0 ? `${mealType.participants.length} participants` : 'No participants'}
                                        </span>
                                    </div>
                                </div>
                                {mealType.envelope && (
                                    <div className="meal-envelope">
                                        <h6>Nutrition Constraints:</h6>
                                        {renderEnvelope(mealType.envelope, false)}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

ProfileNutritionTab.propTypes = {
    profile: PropTypes.object.isRequired,
};

export default ProfileNutritionTab;